import { Card } from '@/components/ui/Card'
import { DataTable, type DataColumn } from '@/components/ui/DataTable'
import { TableSkeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { cn } from '@/lib/cn'
import { fmtDecimal, fmtInt } from '@/lib/format'
import { pctOf, subDecimal, sumDecimal } from '@/lib/num'
import { FEE_PROVIDER_LABEL, FEE_SOURCE, rub2 } from './FeeLabels'

export interface ProviderFeeRow {
  provider: string
  payments: number
  gross_rub: string
  fee_rub: string
  net_rub: string
  effective_pct: string
  /** fee in ₽ per source: provider_actual | provider_quote | rule_estimate */
  sources: Record<string, string>
}

export interface FeeBreakdownTableProps {
  items?: ProviderFeeRow[]
  /** network fee of completed referral payouts in the period */
  payoutFeeRub?: string
  payoutSources?: Record<string, string>
}

/** Fee per source as badges (Факт провайдера / Котировка / По правилу), zero sources hidden.
 *  `align="start"` in a table's status column (left, under its header). */
export function Sources({ sources, align = 'end' }: { sources: Record<string, string>; align?: 'start' | 'end' }) {
  const entries = Object.entries(sources).filter(([, v]) => Number(v) !== 0)
  if (entries.length === 0) return <span className="text-caption text-ink-3">нет данных</span>
  return (
    <div className={cn('flex flex-wrap gap-1.5', align === 'end' && 'justify-end')}>
      {entries.map(([source, value]) => {
        const s = FEE_SOURCE[source] ?? { label: source, tone: 'neutral' as const }
        // One line per badge: the badge has a fixed height, a wrapped label spills out of it.
        return (
          <span key={source} className="whitespace-nowrap">
            <StatusBadge tone={s.tone}>
              {s.label}: <span className="tnum">{rub2(value)} ₽</span>
            </StatusBadge>
          </span>
        )
      })}
    </div>
  )
}

/** On a phone a row is the channel and its fee (the page is about fees) on the first line, the
 *  fee's sources under it, then the other figures as label–value pairs (WP-25): «Платежей»,
 *  «Выручка, ₽», «Чистыми, ₽», «Эффективно, %» — before, the list dropped them and a phone saw
 *  the fee alone. */
const COLUMNS: DataColumn<ProviderFeeRow>[] = [
  { id: 'provider', header: 'Провайдер', type: 'name', cell: (r) => FEE_PROVIDER_LABEL[r.provider] ?? r.provider, mobile: 'title' },
  { id: 'payments', header: 'Платежей', type: 'number', className: 'text-ink-2', cell: (r) => fmtInt(r.payments), mobile: 'field' },
  { id: 'gross', header: 'Выручка, ₽', type: 'money', cell: (r) => rub2(r.gross_rub), mobile: 'field' },
  { id: 'fee', header: 'Комиссия, ₽', unit: '₽', type: 'money', className: 'text-ink-2', cell: (r) => rub2(r.fee_rub), mobile: 'amount' },
  { id: 'net', header: 'Чистыми, ₽', type: 'money', cell: (r) => rub2(r.net_rub), mobile: 'field' },
  { id: 'pct', header: 'Эффективно, %', type: 'percent', className: 'text-ink-2', cell: (r) => fmtDecimal(r.effective_pct), mobile: 'field' },
  { id: 'sources', header: 'Источник цифр', type: 'status', cell: (r) => <Sources sources={r.sources} align="start" />, mobile: 'status' },
]

/** Бухгалтерия → Комиссии: per payment provider for the period — gross, provider fee,
 *  net, the effective rate and where the fee numbers came from. Presentational; every
 *  state is in Storybook (rule 22). */
export function FeeBreakdownTable({ items, payoutFeeRub, payoutSources }: FeeBreakdownTableProps) {
  if (items === undefined)
    return (
      <Card className="p-0">
        <TableSkeleton cols={7} rows={3} />
      </Card>
    )
  // MONEY: exact decimal sums (no float), like ChannelBreakdownTable
  const totalGross = sumDecimal(items.map((r) => r.gross_rub))
  const totalFee = sumDecimal(items.map((r) => r.fee_rub))
  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 ? (
        <Card>
          <p className="text-body text-ink-3">Оплат за период нет</p>
        </Card>
      ) : (
        <DataTable
          label="Комиссии по провайдерам"
          rows={items}
          columns={COLUMNS}
          getRowId={(r) => r.provider}
          totals={{
            label: 'Итого по оплатам',
            cells: {
              payments: fmtInt(items.reduce((a, r) => a + r.payments, 0)),
              gross: rub2(totalGross),
              fee: rub2(totalFee),
              net: rub2(subDecimal(totalGross, totalFee)),
              pct: fmtDecimal(pctOf(totalFee, totalGross)),
            },
          }}
        />
      )}
      {payoutFeeRub !== undefined && (
        <Card className="flex flex-wrap items-center justify-between gap-3 p-3">
          <span className="text-body text-ink-2">
            {FEE_PROVIDER_LABEL.heleket_payout}: сетевая комиссия завершённых выплат, ₽
          </span>
          <div className="flex items-center gap-3">
            <span className="tnum text-right text-ink-1">{rub2(payoutFeeRub)}</span>
            <Sources sources={payoutSources ?? {}} />
          </div>
        </Card>
      )}
    </div>
  )
}
